import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { useSite, wa } from "@/context/ContentContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";

export default function Navbar() {
  const D = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onDark = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-[60] transition-all duration-700 ease-premium ${
          scrolled
            ? "border-b border-charcoal-900/5 bg-white/75 shadow-premium backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="nav-logo" aria-label="Ada Kız Öğrenci Yurdu ana sayfa" className="group">
            <img
              src={onDark ? "/assets/logo-mark-light.png" : "/assets/logo-mark-dark.png"}
              alt="Ada Kız Öğrenci Yurdu"
              className="h-10 w-auto transition-opacity duration-500 group-hover:opacity-80 md:h-11"
            />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Ana menü">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.id}`}
                className={({ isActive }) =>
                  `relative py-2 text-sm font-medium tracking-wide transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-px after:bg-gold after:transition-[width] after:duration-500 after:ease-premium ${
                    onDark
                      ? isActive
                        ? "text-white after:w-full"
                        : "text-white/70 after:w-0 hover:text-white hover:after:w-full"
                      : isActive
                        ? "text-charcoal-900 after:w-full"
                        : "text-charcoal-900/55 after:w-0 hover:text-charcoal-900 hover:after:w-full"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-whatsapp-cta"
              href={wa(D.site)}
              target="_blank"
              rel="noopener noreferrer"
              className={`group hidden items-center gap-2.5 rounded-full py-3 pl-5 pr-6 text-sm font-semibold transition-all duration-500 ease-premium hover:shadow-lift sm:inline-flex ${
                onDark
                  ? "border border-white/30 bg-white/10 text-white backdrop-blur-md hover:bg-white/20"
                  : "bg-charcoal-900 text-white hover:bg-gold-dark"
              }`}
            >
              <WhatsAppIcon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
              Bilgi Al
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Menüyü aç"
              className={`flex h-11 w-11 items-center justify-center rounded-full border backdrop-blur transition-colors duration-300 lg:hidden ${
                onDark
                  ? "border-white/25 bg-white/10 text-white hover:bg-white/20"
                  : "border-charcoal-900/10 bg-white/60 text-charcoal-900 hover:bg-white"
              }`}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] flex flex-col bg-cream-50/95 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <img src="/assets/logo-mark-dark.png" alt="Ada Kız Öğrenci Yurdu" className="h-10 w-auto" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Menüyü kapat"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-charcoal-900/10 text-charcoal-900 transition-colors duration-300 hover:bg-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Mobil menü">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.id}`}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-4 py-3 font-serif text-4xl tracking-tight transition-colors duration-300 ${
                        isActive ? "text-gold-dark" : "text-charcoal-900 hover:text-gold-dark"
                      }`
                    }
                  >
                    <span className="font-accent text-xs tracking-[0.25em] text-gold/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="px-8 pb-12"
            >
              <a
                data-testid="mobile-nav-whatsapp"
                href={wa(D.site)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] py-4 text-base font-semibold text-white shadow-lift"
              >
                <WhatsAppIcon className="h-5 w-5" />
                WhatsApp'tan Bilgi Al
              </a>
              <p className="mt-5 text-center text-sm text-charcoal-900/50">{D.site.phone}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
